import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Subject, of } from 'rxjs';
import { take, switchMap, tap, takeUntil } from 'rxjs/operators';
import { MatTooltipModule } from '@angular/material/tooltip';

import { AppState } from '../../../../../../state/app.state';
import { loadSidenavData, changeActiveSideTab } from '../../../../../../state/action';
import { selectSidenavDataByTabIdAndKey } from '../../../../../../state/selectors';
import { GeneralInnovationActivityResultsService } from '../../../../services/general-innovation-activity-results.service';
import { IInnovationMetricsCompany, IInnovationMetricsSnapshot } from '../../../../../../core/models/results.model';
import { getFlagImage, getFlagColor, getCompetitivenessInfo, getSignalColor, getSignalCardBg, SIGNAL_COLORS } from '../../../../../../shared/utils/bracket.utils';
import { NullableNumberPipe } from '../../../../../../shared/pipes/nullable-number.pipe';
import { CompactUsdPipe } from '../../../../../../shared/pipes/compact-usd.pipe';
import { PdfViewerPopupComponent } from 'src/app/shared/components/pdf-viewer-popup/pdf-viewer-popup.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-exec-summary-v2',
  standalone: true,
  imports: [CommonModule, MatTooltipModule, NullableNumberPipe, CompactUsdPipe],
  templateUrl: './exec-summary-v2.component.html',
  styleUrl: './exec-summary-v2.component.css',
})
export class ExecSummaryV2Component implements OnChanges, OnDestroy {
  @Input() tabId!: string;
  @Input() entityids: string[] | null = null;
  @Input() tab_name: string | null = null;
  @Input() gsubind: string[] | null = null;

  private destroy$ = new Subject<void>();

  snapshot: IInnovationMetricsSnapshot | null = null;
  companyData: IInnovationMetricsCompany | null = null;
  isPublic = false;

  showSpinner = false;
  showDataMissing = false;
  loadingFailed = false;

  constructor(
    private store: Store<AppState>,
    private apiService: GeneralInnovationActivityResultsService,
    private dialog: MatDialog,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['tabId']) {
      this.reset();
      this.loadSnapshot();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private reset(): void {
    this.snapshot = null;
    this.companyData = null;
    this.showSpinner = false;
    this.showDataMissing = false;
    this.loadingFailed = false;
  }

  private loadSnapshot(): void {
    const entityid = this.entityids?.[0];
    if (!entityid) { this.showDataMissing = true; return; }

    const currentTabId = this.tabId;
    this.showSpinner = true;

    this.store.select(selectSidenavDataByTabIdAndKey(currentTabId, 'innovation_snapshot')).pipe(
      take(1),
      switchMap(cached => {
        if (cached) {
          if (this.tabId === currentTabId) {
            this.applySnapshot(cached, entityid);
            this.showSpinner = false;
          }
          return of(null);
        }
        return this.apiService.getInnovationSnapshot(entityid, currentTabId).pipe(
          takeUntil(this.destroy$),
          tap(res => {
            const data = res.body?.payload;
            if (!res.ok || !data?.companies?.length) {
              if (this.tabId === currentTabId) {
                this.showSpinner = false;
                this.showDataMissing = true;
              }
              return;
            }
            const targetTabId = data.tabId ?? currentTabId;
            this.store.dispatch(loadSidenavData({ tabId: targetTabId, sidenavKey: 'innovation_snapshot', data }));
            if (this.tabId === currentTabId && targetTabId === currentTabId) {
              this.showSpinner = false;
              this.applySnapshot(data, entityid);
            }
          }),
        );
      }),
    ).subscribe({ error: () => { if (this.tabId === currentTabId) { this.showSpinner = false; this.loadingFailed = true; } } });
  }

  private applySnapshot(data: IInnovationMetricsSnapshot, entityid: string): void {
    this.snapshot = data;
    this.companyData = data.companies.find(c => c.entityid === entityid) ?? null;
    this.isPublic = !!this.companyData?.companytype?.toLowerCase().includes('public');
  }

  // Backend sends NaN as " ", so only accept real numbers
  get portfolioValue(): number | null {
    const v = this.companyData?.portfolio_value_usd;
    return typeof v === 'number' && isFinite(v) ? v : null;
  }

  goToTopCompetitors(): void {
    this.store.dispatch(changeActiveSideTab({
      sideTab: this.isPublic ? 'company_public_competitors' : 'company_private_competitors'
    }));
  }

  flagImage = getFlagImage;
  flagColor = getFlagColor;
  competitivenessInfo = getCompetitivenessInfo;
  signalColor = getSignalColor;
  signalCardBg = getSignalCardBg;
  readonly inactiveSignalColor = '#9ca3af';
  signalActiveColor(signal: string | undefined, forSignal: string): string {
    return signal === forSignal ? getSignalColor(forSignal) : this.inactiveSignalColor;
  }

  rankChangeLabel(change: number | null): string {
    if (change === null) return '—';
    if (change > 0) return `↑ ${change}`;
    if (change < 0) return `↓ ${Math.abs(change)}`;
    return '—';
  }

  get signalSentenceParts(): [string, string] | null {
    const s = this.companyData?.signal_sentence;
    if (!s) return null;
    const parts = s.split('\n\n');
    return parts.length >= 2 ? [parts[0], parts[1]] : [parts[0], ''];
  }


  openInfoPopup(activeKey: 'company_innovation_score' | 'company_sector_perf'): void {
    const sectorName = this.snapshot?.gics_sector_name || '';
    const sectorLabel = this.snapshot?.peer_group_display || sectorName || 'Sector';
    this.dialog.open(PdfViewerPopupComponent, {
      data: {
        tabs: [
          { label: 'Innovation Score', key: 'company_innovation_score' },
          {
            label: `Performance in ${sectorLabel}`,
            key: 'company_sector_perf',
            params: sectorName ? { sector: sectorName } : undefined,
          },
        ],
        activeKey,
      },
      width: '80vw',
      height: '80vh',
      maxWidth: '90vw',
      panelClass: 'pdf-viewer-dialog-panel',
    });
  }
}
